import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Target, 
  Plus, 
  Edit3, 
  ExternalLink, 
  BookOpen, 
  Play, 
  CheckCircle2, 
  Clock, 
  TrendingUp,
  Lightbulb,
  Users,
  Calendar,
  Star,
  AlertCircle,
  Loader2
} from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { toast } from "sonner";
import { goalsService } from "@/services/goalsService";
import { format } from "date-fns";
import AddGrowthGoalModal from "@/components/modals/AddGrowthGoalModal";
import EditGrowthGoalModal from "./EditGrowthGoalModal";
import GoalResourcesModal from "./GoalResourcesModal";

interface GrowthGoal {
  id: string;
  user_id: string;
  title: string;
  description?: string;
  category: string;
  due_date?: string;
  progress: number;
  status?: 'active' | 'completed' | 'paused' | 'cancelled';
  priority?: 'low' | 'medium' | 'high';
  ai_suggested?: boolean;
  skills_needed?: string[];
  milestones?: any[];
  recommended_resources?: Resource[];
  peer_connections?: string[];
  created_at: string;
  updated_at: string;
  reminder_frequency?: string;
  last_reminder_sent?: string;
  notification_enabled?: boolean;
}

interface Resource {
  id?: string;
  title: string;
  description?: string;
  url?: string;
  resource_type: 'article' | 'video' | 'course' | 'book' | 'tool' | 'person' | 'project';
  ai_recommended: boolean;
  completed?: boolean;
}

interface AIGoalSuggestion {
  title: string;
  category: string;
  description: string;
  skills_needed: string[];
  confidence: number;
  resources?: Resource[];
}

export default function EnhancedGrowthGoals() {
  const { user } = useUser();
  
  // State
  const [goals, setGoals] = useState<GrowthGoal[]>([]);
  const [aiSuggestions, setAiSuggestions] = useState<AIGoalSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingSuggestions, setIsGeneratingSuggestions] = useState(false);
  
  // Modals
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isResourcesModalOpen, setIsResourcesModalOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState<GrowthGoal | null>(null);

  useEffect(() => {
    if (user?.id) {
      loadGoals();
      generateAISuggestions();
    }
  }, [user?.id]);

  const loadGoals = async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      const response = await goalsService.getGoals();

      // API returns data directly or { goals: [...] }
      const goalsData = Array.isArray(response) ? response : (response?.goals || []);
      setGoals(goalsData.map((goal: any) => ({
        ...goal,
        id: goal.id,
        user_id: goal.userId || goal.user_id,
        title: goal.title,
        description: goal.description,
        category: goal.category,
        due_date: goal.dueDate || goal.due_date,
        progress: goal.progress,
        status: goal.status || 'active',
        priority: goal.priority || 'medium',
        ai_suggested: goal.aiSuggested || goal.ai_suggested || false,
        skills_needed: goal.skillsNeeded || goal.skills_needed || [],
        milestones: goal.milestones || [],
        recommended_resources: goal.recommendedResources || goal.recommended_resources || [],
        peer_connections: goal.peerConnections || goal.peer_connections || [],
        created_at: goal.createdAt || goal.created_at,
          updated_at: goal.updatedAt || goal.updated_at,
          reminder_frequency: goal.reminderFrequency || goal.reminder_frequency,
          last_reminder_sent: goal.lastReminderSent || goal.last_reminder_sent,
          notification_enabled: goal.notificationEnabled !== false,
        })));
    } catch (error) {
      console.error('Error loading goals:', error);
      toast.error('Failed to load goals');
    } finally {
      setIsLoading(false);
    }
  };

  // ALIGNED WITH web_old: generateAISuggestions with backend API
  // Backend endpoint: GET /goals/suggestions
  // MIGRATED FROM web_old/src/components/growth/EnhancedGrowthGoals.tsx::generateAISuggestions()
  const generateAISuggestions = async () => {
    if (!user?.id) return;
    
    setIsGeneratingSuggestions(true);
    try {
      // ALIGNED WITH web_old: Call backend endpoint (lines 192-195)
      // Backend now returns format matching web_old exactly (no mapping needed)
      const response = await goalsService.getSuggestions();
      const suggestions = response?.suggestions || [];
      
      // ALIGNED WITH web_old: Backend returns format directly matching AIGoalSuggestion interface
      // No mapping needed - backend format matches web_old format exactly
      setAiSuggestions(Array.isArray(suggestions) ? suggestions.slice(0, 3) : []);
    } catch (error) {
      console.error('Error generating AI suggestions:', error);
      // ALIGNED WITH web_old: Show error toast (line 221)
      toast.error('Failed to generate suggestions');
      // ALIGNED WITH web_old: Backend handles fallback, so we just set empty array
      setAiSuggestions([]);
    } finally {
      setIsGeneratingSuggestions(false);
    }
  };

  const handleAddGoal = async (goalData: any) => {
    if (!user?.id) return;

    try {
      // Format dueDate to YYYY-MM-DD if present
      let formattedDueDate: string | undefined;
      if (goalData.dueDate) {
        const date = new Date(goalData.dueDate);
        formattedDueDate = date.toISOString().split('T')[0];
      }

      const newGoal = await goalsService.createGoal({
        title: goalData.title,
        description: goalData.description,
        category: goalData.category,
        dueDate: formattedDueDate,
        progress: goalData.progress || 0,
        priority: goalData.priority || 'medium',
        skillsNeeded: goalData.skills_needed || []
      });

      if (newGoal && newGoal.id) {
        setGoals(prev => [newGoal, ...prev]);
        toast.success('Goal added successfully!');
      } else {
        throw new Error('Failed to create goal');
      }
    } catch (error) {
      console.error('Error adding goal:', error);
      toast.error('Failed to add goal');
    }
  };

  const handleAcceptSuggestion = async (suggestion: AIGoalSuggestion) => {
    if (!user?.id) return;

    try {
      const targetDate = new Date();
      targetDate.setMonth(targetDate.getMonth() + 2); // Default 2 months
      const formattedDueDate = targetDate.toISOString().split('T')[0];

      const newGoal = await goalsService.createGoal({
        title: suggestion.title,
        description: suggestion.description,
        category: suggestion.category,
        dueDate: formattedDueDate,
        priority: 'medium',
        skillsNeeded: suggestion.skills_needed || []
      });

      if (!newGoal || !newGoal.id) {
        throw new Error('Failed to create goal');
      }

      const goalId = newGoal.id;

      // Add AI-recommended resources if any
      if (suggestion.resources && suggestion.resources.length > 0) {
        for (const resource of suggestion.resources) {
          await goalsService.addResource(goalId, {
            title: resource.title,
            description: resource.description,
            url: resource.url,
            resourceType: resource.resource_type,
            aiRecommended: true
          });
        }
      }
      
      setGoals(prev => [newGoal, ...prev]);
      setAiSuggestions(prev => prev.filter(s => s.title !== suggestion.title));
      toast.success('Goal added from AI suggestion!');
    } catch (error) {
      console.error('Error accepting suggestion:', error);
      toast.error('Failed to add suggested goal');
    }
  };

  const updateProgress = async (goalId: string, newProgress: number) => {
    try {
      await goalsService.updateGoal(goalId, {
        progress: newProgress,
        status: newProgress === 100 ? 'completed' : undefined
      });

      setGoals(prev => prev.map(goal => 
        goal.id === goalId 
          ? { ...goal, progress: newProgress, status: newProgress === 100 ? 'completed' : goal.status }
          : goal
      ));

      toast.success('Progress updated!');
    } catch (error) {
      console.error('Error updating progress:', error);
      toast.error('Failed to update progress');
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700 border-red-200';
      case 'medium': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'low': return 'bg-gray-100 text-gray-700 border-gray-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'active': return <Play className="h-4 w-4 text-blue-500" />;
      case 'paused': return <Clock className="h-4 w-4 text-yellow-500" />;
      default: return <Target className="h-4 w-4 text-gray-500" />;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Growth Goals
              </CardTitle>
              <CardDescription>
                AI-powered goal setting and progress tracking for your development journey
              </CardDescription>
            </div>
            <Button onClick={() => setIsAddModalOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Goal
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          <Tabs defaultValue="active">
            <TabsList className="mb-4">
              <TabsTrigger value="active">Active Goals</TabsTrigger>
              <TabsTrigger value="suggestions">AI Suggestions</TabsTrigger>
              <TabsTrigger value="completed">Completed</TabsTrigger>
            </TabsList>

            <TabsContent value="active">
              <ScrollArea className="h-[400px]">
                <div className="space-y-4">
                  {goals.filter(g => (g.status || 'active') === 'active').map((goal) => (
                    <Card key={goal.id} className="border-l-4 border-l-blue-500">
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold">{goal.title}</h3>
                              {goal.ai_suggested && (
                                <Badge variant="secondary" className="text-xs">
                                  <Lightbulb className="h-3 w-3 mr-1" />
                                  AI Suggested
                                </Badge>
                              )}
                              <Badge className={`text-xs ${getPriorityColor(goal.priority)}`}>
                                {goal.priority}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">{goal.description}</p>
                            
                            <div className="flex items-center gap-4 mb-3">
                              <div className="flex-1">
                                <div className="flex justify-between text-sm mb-1">
                                  <span>Progress</span>
                                  <span>{goal.progress}%</span>
                                </div>
                                <Progress value={goal.progress} className="h-2" />
                              </div>
                              {goal.due_date && (
                                <div className="text-sm text-muted-foreground flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  {format(new Date(goal.due_date), 'MMM d')}
                                </div>
                              )}
                            </div>

                            {goal.skills_needed && goal.skills_needed.length > 0 && (
                              <div className="flex flex-wrap gap-1 mb-2">
                                {goal.skills_needed.map((skill) => (
                                  <Badge key={skill} variant="outline" className="text-xs">
                                    {skill}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>

                          <div className="flex items-center gap-2 ml-4">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedGoal(goal);
                                setIsResourcesModalOpen(true);
                              }}
                            >
                              <BookOpen className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedGoal(goal);
                                setIsEditModalOpen(true);
                              }}
                            >
                              <Edit3 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>

                        {/* Quick progress actions */}
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => updateProgress(goal.id, Math.min(100, goal.progress + 10))}
                            disabled={goal.progress >= 100}
                          >
                            +10%
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => updateProgress(goal.id, Math.min(100, goal.progress + 25))}
                            disabled={goal.progress >= 100}
                          >
                            +25%
                          </Button>
                          {goal.progress < 100 && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => updateProgress(goal.id, 100)}
                            >
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Complete
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  {goals.filter(g => (g.status || 'active') === 'active').length === 0 && (
                    <div className="text-center py-8">
                      <Target className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground">No active goals yet. Add one to get started!</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="suggestions">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    AI-powered goal suggestions based on your recent patterns and growth areas
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={generateAISuggestions}
                    disabled={isGeneratingSuggestions}
                  >
                    {isGeneratingSuggestions ? (
                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    ) : (
                      <TrendingUp className="h-3 w-3 mr-1" />
                    )}
                    Refresh
                  </Button>
                </div>

                <ScrollArea className="h-[400px]">
                  <div className="space-y-4">
                    {aiSuggestions.map((suggestion, index) => (
                      <Card key={index} className="border-l-4 border-l-purple-500">
                        <CardContent className="pt-4">
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold">{suggestion.title}</h3>
                                <Badge variant="secondary" className="text-xs">
                                  <Star className="h-3 w-3 mr-1" />
                                  {Math.round(suggestion.confidence * 100)}% match
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground mb-2">{suggestion.description}</p>
                              
                              {suggestion.skills_needed.length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-3">
                                  {suggestion.skills_needed.map((skill) => (
                                    <Badge key={skill} variant="outline" className="text-xs">
                                      {skill}
                                    </Badge>
                                  ))}
                                </div>
                              )}

                              {suggestion.resources && suggestion.resources.length > 0 && (
                                <div className="text-xs text-muted-foreground mb-2">
                                  <BookOpen className="h-3 w-3 inline mr-1" />
                                  {suggestion.resources.length} recommended resources included
                                </div>
                              )}
                            </div>

                            <Button
                              onClick={() => handleAcceptSuggestion(suggestion)}
                              size="sm"
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Add Goal
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}

                    {isGeneratingSuggestions && (
                      <div className="text-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">Generating personalized suggestions...</p>
                      </div>
                    )}

                    {!isGeneratingSuggestions && aiSuggestions.length === 0 && (
                      <div className="text-center py-8">
                        <Lightbulb className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground">No suggestions available. Try refreshing or complete more check-ins for better recommendations.</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </div>
            </TabsContent>

            <TabsContent value="completed">
              <ScrollArea className="h-[400px]">
                <div className="space-y-4">
                  {goals.filter(g => g.status === 'completed').map((goal) => (
                    <Card key={goal.id} className="border-l-4 border-l-green-500 opacity-80">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 mb-1">
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                          <h3 className="font-semibold">{goal.title}</h3>
                          <Badge className="text-xs bg-green-100 text-green-700">
                            Completed
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{goal.description}</p>
                      </CardContent>
                    </Card>
                  ))}

                  {goals.filter(g => g.status === 'completed').length === 0 && (
                    <div className="text-center py-8">
                      <CheckCircle2 className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground">No completed goals yet. Keep working towards your active goals!</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <AddGrowthGoalModal
        isOpen={isAddModalOpen}
        onOpenChange={setIsAddModalOpen}
        onSubmit={handleAddGoal}
      />

      {selectedGoal && (
        <>
          <EditGrowthGoalModal
            isOpen={isEditModalOpen}
            onOpenChange={setIsEditModalOpen}
            goal={selectedGoal}
            onUpdate={loadGoals}
          />

          <GoalResourcesModal
            isOpen={isResourcesModalOpen}
            onOpenChange={setIsResourcesModalOpen}
            goal={selectedGoal}
          />
        </>
      )}
    </div>
  );
}